import React, { useEffect, useState, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Table, Btn, Modal, Input, Select, Badge, Card, Textarea } from "@/components/UI";
import { Plus, ShieldCheck, Truck, Route, Printer, FileText, History, Mail, Beaker, ClipboardList, Download, Upload, Trash2, Play, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { FilterAndReportBar } from "@/components/FilterAndReportBar";
import TelegramSendButton from "@/components/TelegramSendButton";
import EmailSendButton from "@/components/EmailSendButton";
import { openPdfPreview } from "@/lib/pdfPreview";
export function QualityPage() {
  const { user, isAdmin, isSystemAuthority } = useAuth();
  const canEditQC = isAdmin || isSystemAuthority;
  const [tab, setTab] = useState("pending_lots");
  const [lots, setLots] = useState([]);
  const [prods, setProds] = useState([]);
  const [qcRecords, setQcRecords] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [filteredQc, setFilteredQc] = useState([]);
  const [qcOpen, setQcOpen] = useState(false);
  const [qcForm, setQcForm] = useState({ kind: "raw", reference_type: "lot", reference_id: "", specs: [] });
  // iter316 — Hammadde QC detay/düzenle modal state
  const [qcDetail, setQcDetail] = useState(null);
  const [qcEditing, setQcEditing] = useState(false);
  const [qcBackfillBusy, setQcBackfillBusy] = useState(false);
  // GMP zorunluluğu: QC onayı öncesi Şahit Numune alındığı kullanıcı tarafından teyit edilmelidir.
  const [retainedSample, setRetainedSample] = useState(false);

  const load = () => Promise.all([
    api.get("/lots", { status: "awaiting_qc" }),
    api.get("/production-orders"),
    api.get("/qc"),
    api.get("/materials"),
  ]).then(([l, p, q, m]) => { setLots(l); setProds(p); setQcRecords(q); setMaterials(m); });
  useEffect(() => { load(); }, []);

  const approveLot = async (id) => { await api.post(`/lots/${id}/approve`); load(); };
  const blockLot = async (id) => { const r = prompt("Blokaj nedeni:"); if (r) { await api.post(`/lots/${id}/block?reason=${encodeURIComponent(r)}`); load(); } };

  const openQC = async (ref_type, ref_id, kind) => {
    let defaultSpecs = [];
    // Auto-populate from formula when quality control is on a production order
    if (ref_type === "production_order") {
      try {
        const po = await api.get(`/production-orders/${ref_id}`);
        if (po?.formula_id) {
          const tpl = await api.get(`/qc/from-formula/${po.formula_id}`);
          defaultSpecs = (tpl.qc_specs || []).map(s => ({ ...s, measured: null, text_result: null, passed: null }));
        }
      } catch (e) {}
    }
    if (defaultSpecs.length === 0) {
      defaultSpecs = kind === "bulk" || kind === "finished"
        ? [{ name: "pH", type: "number", lower: 5, target: 6.5, upper: 8, unit: "" }, { name: "Viskozite", type: "number", lower: 500, target: 1000, upper: 2000, unit: "cP" }, { name: "Görünüm", type: "text", expected_text: "" }]
        : [{ name: "Görünüm", type: "text" }, { name: "Koku", type: "text" }, { name: "Renk", type: "text" }];
    }
    // iter317 — Bitmiş Ürün QC için Etiket Okunurluğu + Ambalaj Uygunluğu zorunlu
    // olarak eklensin (formül qc_specs bu alanları içermiyorsa).
    if (kind === "finished") {
      const names = new Set(defaultSpecs.map(s => (s.name || "").toLowerCase().trim()));
      if (!names.has("etiket okunurluğu") && !names.has("etiket okunurlugu")) {
        defaultSpecs.push({ name: "Etiket Okunurluğu", type: "yesno", expected_text: "Uygun", required: true, text_result: "Uygun", passed: true });
      }
      if (!names.has("ambalaj uygunluğu") && !names.has("ambalaj uygunlugu")) {
        defaultSpecs.push({ name: "Ambalaj Uygunluğu", type: "yesno", expected_text: "Uygun", required: true, text_result: "Uygun", passed: true });
      }
    }
    setQcForm({ kind, reference_type: ref_type, reference_id: ref_id, specs: defaultSpecs });
    setRetainedSample(false); // her açılışta sıfırla — GMP
    setQcOpen(true);
  };
  const saveQC = async () => { await api.post("/qc", qcForm); setQcOpen(false); load(); };
  const finalizeQC = async (id, result) => { await api.post(`/qc/${id}/finalize`, { result }); load(); };

  // iter316 — Hammadde QC detay / düzenle
  const openQcDetail = (qc) => {
    const base = {
      koku: { result: "Uygun" }, gorunum: { result: "Uygun" },
      ph: { value: null, result: "Uygulanmaz" }, viscosity: { value: null, result: "Uygulanmaz" },
      label_readability: { result: "Uygun" }, packaging_suitability: { result: "Uygun" },
    };
    setQcDetail({ ...qc, raw_criteria: { ...base, ...(qc.raw_criteria || {}) } });
    setQcEditing(false);
  };
  const saveQcCriteria = async () => {
    try {
      const r = await api.put(`/qc/${qcDetail.id}`, {
        koku: qcDetail.raw_criteria.koku,
        gorunum: qcDetail.raw_criteria.gorunum,
        ph: qcDetail.raw_criteria.ph,
        viscosity: qcDetail.raw_criteria.viscosity,
        label_readability: qcDetail.raw_criteria.label_readability,
        packaging_suitability: qcDetail.raw_criteria.packaging_suitability,
        notes: qcDetail.criteria_notes || "",
      });
      alert(`Kaydedildi. Genel Sonuç: ${r.result === "pass" ? "UYGUN" : "UYGUN DEĞİL"}`);
      setQcEditing(false);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Güncellenemedi"); }
  };
  const runBackfill = async () => {
    if (!window.confirm("Eski onaylı lotlar için eksik Hammadde QC kayıtlarını oluştur? (idempotent)")) return;
    setQcBackfillBusy(true);
    try {
      const r = await api.post("/qc/backfill-raw-material", {});
      alert(`Backfill tamamlandı. Oluşturulan: ${r.created}, Atlandı (zaten var): ${r.skipped}`);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Backfill başarısız"); }
    finally { setQcBackfillBusy(false); }
  };
  const downloadTabListPDF = async () => {
    const kindMap = { pending_lots: "raw", history: null, pending_bulk: "bulk", pending_fp: "finished" };
    const kind = kindMap[tab];
    if (kind === undefined) return;
    try {
      const url = kind ? `/qc/list/pdf?kind=${kind}` : "/qc/list/pdf";
      const blob = await api.getBlob(url);
      openPdfPreview(blob, `QC_Liste_${kind || "all"}.pdf`);
    } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
  };

  const bulkPending = prods.filter(p => p.status === "awaiting_qc_bulk");
  const fpPending = prods.filter(p => p.status === "awaiting_qc_finished");
  const mname = id => materials.find(m => m.id === id)?.name || "—";

  return (
    <div>
      <PageHeader title="Kalite Kontrol" description="Ham madde, bulk ve bitmiş ürün kalite denetimi" />
      <FilterAndReportBar
        reportType="quality" rows={qcRecords}
        statuses={[{value:"pass",label:"Başarılı"},{value:"fail",label:"Reddedildi"},{value:"pending",label:"Bekliyor"}]}
        dateAccessor={r => r.created_at}
        productAccessor={r => r.qc_no || ""}
        statusAccessor={r => r.result}
        onFiltered={setFilteredQc}
      />
      <div className="mb-4 flex gap-2 flex-wrap items-center">
        {[
          { k: "pending_lots", l: `Ham Madde (${lots.length})`, c: "cyan" },
          { k: "pending_bulk", l: `Bulk (${bulkPending.length})`, c: "violet" },
          { k: "pending_fp", l: `Bitmiş Ürün (${fpPending.length})`, c: "emerald" },
          { k: "history", l: `Geçmiş (${qcRecords.length})`, c: "slate" },
          { k: "test_tracking", l: "Test Takip", c: "indigo" },
          { k: "internal_tests", l: "Firma İçi Testler", c: "fuchsia" },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`qc-tab-${t.k}`}
            className={`px-4 py-2 text-sm rounded-lg border transition-all ${tab === t.k ? `bg-${t.c}-100 border-${t.c}-300 text-${t.c}-800 font-semibold shadow-sm` : `bg-white border-slate-200 text-slate-600 hover:bg-${t.c}-50 hover:border-${t.c}-200`}`}>
            {t.l}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          {["pending_lots", "pending_bulk", "pending_fp", "history"].includes(tab) && (
            <Btn size="sm" variant="secondary" onClick={downloadTabListPDF} data-testid="qc-tab-pdf"
              title="Bu sekmenin listesini PDF indir">
              <FileText size={12}/> Sekme PDF
            </Btn>
          )}
          {canEditQC && (
            <Btn size="sm" variant="secondary" onClick={runBackfill} disabled={qcBackfillBusy} data-testid="qc-backfill-btn"
              title="Eski onaylı lotlar için eksik Hammadde QC kayıtlarını oluştur (idempotent)">
              {qcBackfillBusy ? "..." : "Eski QC Backfill"}
            </Btn>
          )}
        </div>
      </div>

      {tab === "test_tracking" && <QcTestTracking internalOnly={false}/>}
      {tab === "internal_tests" && <QcTestTracking internalOnly={true}/>}

      {tab === "pending_lots" && (
        <Table columns={[
          { label: "Lot / Ted. Lot", render: r => <span className="font-mono text-xs">{r.lot_code}</span> },
          { label: "Malzeme", render: r => mname(r.material_id) },
          { label: "Miktar", key: "qty_available" },
          { label: "İşlem", render: r => (
            <div className="flex gap-1">
              <Btn size="sm" variant="secondary" onClick={() => openQC("lot", r.id, "raw")} data-testid={`qc-raw-${r.id}`}>QC Formu</Btn>
              <Btn size="sm" onClick={() => approveLot(r.id)} data-testid={`approve-raw-${r.id}`} variant="approve">Onayla</Btn>
              <Btn size="sm" variant="danger" onClick={() => blockLot(r.id)}>Blokla</Btn>
            </div>
          ) },
        ]} rows={lots} />
      )}

      {tab === "pending_bulk" && (
        <Table columns={[
          { label: "Üretim", render: r => <span className="font-mono text-xs">{r.prod_no}</span> },
          { label: "Gerçek Bulk", render: r => `${r.actual_bulk_kg} kg` },
          { label: "Verim", render: r => `${r.yield_pct}%` },
          { label: "İşlem", render: r => (
            <div className="flex gap-1">
              <Btn size="sm" variant="secondary" onClick={() => openQC("production_order", r.id, "bulk")} data-testid={`qc-bulk-${r.id}`}>QC Formu</Btn>
              {/* Iter 202 §19 — Bulk QC controlled PDF (preview-first). */}
              <Btn size="sm" variant="ghost" onClick={async () => {
                try { const blob = await api.getBlob(`/qc/bulk/${r.id}/pdf`); openPdfPreview(blob, `BulkQC_${r.prod_no}.pdf`); }
                catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
              }} data-testid={`qc-bulk-pdf-${r.id}`} title="Bulk QC PDF"><FileText size={12}/></Btn>
            </div>
          ) },
        ]} rows={bulkPending} />
      )}

      {tab === "pending_fp" && (
        <Table columns={[
          { label: "Üretim", render: r => <span className="font-mono text-xs">{r.prod_no}</span> },
          { label: "Lot", render: r => <span className="font-mono text-xs">{r.finished_lot_code}</span> },
          { label: "Adet", render: r => r.filling_result?.accepted_units || "—" },
          { label: "İşlem", render: r => (
            <div className="flex gap-1">
              <Btn size="sm" variant="secondary" onClick={() => openQC("production_order", r.id, "finished")} data-testid={`qc-fp-${r.id}`}>QC Formu</Btn>
              {/* Iter 202 §21 — Finished Product QC controlled PDF (preview-first). */}
              <Btn size="sm" variant="ghost" onClick={async () => {
                try { const blob = await api.getBlob(`/qc/finished/${r.id}/pdf`); openPdfPreview(blob, `FinishedQC_${r.prod_no}.pdf`); }
                catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
              }} data-testid={`qc-fp-pdf-${r.id}`} title="Bitmiş Ürün QC PDF"><FileText size={12}/></Btn>
            </div>
          ) },
        ]} rows={fpPending} />
      )}

      {tab === "history" && (
        <Table columns={[
          { label: "QC No", render: r => {
              if (r.reference_type === "lot") {
                const lot = (lots.find && lots.find(x => x.id === r.reference_id)) || {};
                return <span className="font-mono text-xs" title={r.qc_no}>{lot.lot_code || r.qc_no}</span>;
              }
              return <span className="font-mono text-xs">{r.qc_no}</span>;
            } },
          { label: "Tip", key: "kind" },
          { label: "Ref", render: r => <span className="text-xs">{r.reference_id?.slice(0, 8)}</span> },
          { label: "Sonuç", render: r => <Badge level={r.result === "pass" ? "ok" : r.result === "fail" ? "missing" : "attention"}>{r.result}</Badge> },
          { label: "Tarih", render: r => (r.created_at || "").slice(0, 10) },
          { label: "İşlem", render: r => (
            <div className="flex gap-1">
              <Btn size="sm" variant="secondary" data-testid={`qc-detail-${r.id}`} title="Detay / Düzenle" onClick={() => openQcDetail(r)}>
                <ClipboardList size={12}/>
              </Btn>
              <Btn size="sm" variant="secondary" data-testid={`qc-pdf-${r.id}`} title="QC PDF" onClick={async () => {
                try {
                  const blob = await api.getBlob(`/qc/${r.id}/pdf`);
                  openPdfPreview(blob, `qc_${r.qc_no || r.id.slice(0,8)}.pdf`);
                } catch (e) { alert("PDF alınamadı"); }
              }}><FileText size={12}/></Btn>
            </div>
          ) },
        ]} rows={filteredQc.length > 0 || qcRecords.length === 0 ? filteredQc : qcRecords} />
      )}

      <Modal open={qcOpen} onClose={() => setQcOpen(false)} title={`QC Formu — ${qcForm.kind}`} size="lg">
        <div className="text-xs text-slate-500 bg-indigo-50/60 border border-indigo-200/60 rounded p-2 mb-3">
          <b className="text-indigo-800">Referans QC Değerleri</b> (formüller sayfasından, salt-okunur) yanına ölçümlerinizi girin. Kabul aralığı dışındaki ölçümler onay öncesi uyarı gösterir.
        </div>
        {/* Iter 204 §2 — Test Eden / Onaylayan kimlik chip (mevcut oturum kullanıcısı, salt-okunur snapshot). */}
        <div className="text-[11px] flex items-center gap-3 mb-3 px-2 py-1.5 rounded bg-slate-50 border border-slate-200" data-testid="qc-user-identity">
          <span><b className="text-slate-700">Test Eden:</b> <span className="text-indigo-700 font-semibold">{user?.name || user?.username || "—"}</span></span>
          <span className="text-slate-400">|</span>
          <span><b className="text-slate-700">Onaylayan (Kabul/Reddet):</b> <span className="text-indigo-700 font-semibold">{user?.name || user?.username || "—"}</span></span>
        </div>
        <div className="space-y-3 mb-4">
          {qcForm.specs.map((s, i) => {
            const isYesNo = s.type === "yesno";
            const isText = s.type === "text";
            // iter317 — Etiket/Ambalaj gibi yesno specs için select UI
            if (isYesNo) {
              const isFail = (s.text_result || "Uygun") === "Uygun Değil";
              return (
                <div key={i} className={`rounded-lg p-2 border ${isFail ? "bg-red-50/50 border-red-300" : "bg-emerald-50/40 border-emerald-200"}`} data-testid={`qc-spec-row-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-slate-800 flex-1">{s.name}{s.required && <span className="text-rose-600 ml-1">*</span>}</span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 border border-indigo-200">Beklenen: Uygun</span>
                    <Select value={s.text_result || "Uygun"} onChange={e => {
                      const val = e.target.value;
                      const sp = [...qcForm.specs];
                      sp[i] = { ...sp[i], text_result: val, passed: val === "Uygun" };
                      setQcForm({ ...qcForm, specs: sp });
                    }} data-testid={`qc-spec-${i}`}>
                      <option value="Uygun">Uygun</option>
                      <option value="Uygun Değil">Uygun Değil</option>
                    </Select>
                    {isFail && <span className="text-[10px] font-semibold px-2 py-1 bg-red-500 text-white rounded whitespace-nowrap" data-testid={`qc-eval-${i}`}>Uygun Değil ✗</span>}
                    {!isFail && <span className="text-[10px] font-semibold px-2 py-1 bg-emerald-500 text-white rounded whitespace-nowrap" data-testid={`qc-eval-${i}`}>Uygun ✓</span>}
                  </div>
                </div>
              );
            }
            const outOfRange = !isText && s.lower != null && s.upper != null && s.measured != null && !Number.isNaN(s.measured) && (s.measured < s.lower || s.measured > s.upper);
            const referenceLabel = isText
              ? (s.expected_text ? `Beklenen: "${s.expected_text}"` : "Referans yok")
              : `Referans: ${s.lower ?? "—"} — ${s.upper ?? "—"} ${s.unit || ""} ${s.target != null ? `(hedef: ${s.target})` : ""}`;
            return (
              <div key={i} className={`rounded-lg p-2 border ${outOfRange ? "bg-red-50/50 border-red-200" : "bg-slate-50 border-slate-200"}`} data-testid={`qc-spec-row-${i}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <input value={s.name || ""} onChange={e => { const sp = [...qcForm.specs]; sp[i] = { ...sp[i], name: e.target.value }; setQcForm({ ...qcForm, specs: sp }); }}
                    className="text-sm font-semibold text-slate-800 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-indigo-400 outline-none flex-1 mr-2" placeholder="Parametre"/>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${outOfRange ? "bg-red-100 text-red-700 border border-red-300" : "bg-indigo-100 text-indigo-700 border border-indigo-200"}`} data-testid={`qc-ref-${i}`}>
                    {referenceLabel}
                  </span>
                </div>
                {isText ? (
                  <div className="grid grid-cols-2 gap-2 items-center">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 pl-1">Beklenen (Formül)</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 pl-1">Ölçüm / Metin</div>
                    <input value={s.expected_text || ""} className="px-2 py-1.5 text-xs border border-slate-200 rounded bg-white text-slate-600" placeholder="—" readOnly/>
                    <input value={s.text_result || ""} onChange={e => { const sp = [...qcForm.specs]; sp[i] = { ...sp[i], text_result: e.target.value }; setQcForm({ ...qcForm, specs: sp }); }}
                      className="px-2 py-1.5 text-xs border border-emerald-300 rounded bg-white font-semibold" placeholder="Ölçüm giriniz..." data-testid={`qc-spec-${i}`}/>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 pl-1">Alt Limit</div>
                      <input type="number" value={s.lower ?? ""} className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded bg-white text-slate-600 font-mono" readOnly/>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 pl-1">Üst Limit</div>
                      <input type="number" value={s.upper ?? ""} className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded bg-white text-slate-600 font-mono" readOnly/>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 pl-1">Birim</div>
                      <input value={s.unit || ""} className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded bg-white text-slate-600 font-mono" readOnly/>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-emerald-700 pl-1 font-semibold">Ölçüm *</div>
                      <input type="number" step="0.01" value={s.measured ?? ""} onChange={e => {
                        const val = parseFloat(e.target.value);
                        const sp = [...qcForm.specs];
                        // Auto-evaluate: if measured within [lower, upper] → passed = true; outside → passed = false
                        let passed = null;
                        if (!Number.isNaN(val) && s.lower != null && s.upper != null) {
                          passed = (val >= s.lower && val <= s.upper);
                        }
                        sp[i] = { ...sp[i], measured: val, passed };
                        setQcForm({ ...qcForm, specs: sp });
                      }}
                        className={`w-full px-2 py-1.5 text-xs border rounded font-semibold font-mono ${outOfRange ? "border-red-400 bg-red-50 text-red-700" : "border-emerald-300 bg-white"}`}
                        placeholder="Girin" data-testid={`qc-spec-${i}`}/>
                    </div>
                    <div className="col-span-4 flex items-center justify-between gap-2">
                      <input value={s.text_result || ""} onChange={e => { const sp = [...qcForm.specs]; sp[i] = { ...sp[i], text_result: e.target.value }; setQcForm({ ...qcForm, specs: sp }); }}
                        className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded" placeholder="Not (isteğe bağlı)"/>
                      {s.passed === true && <span className="text-[10px] font-semibold px-2 py-1 bg-emerald-500 text-white rounded whitespace-nowrap" data-testid={`qc-eval-${i}`}>Uygun ✓</span>}
                      {s.passed === false && <span className="text-[10px] font-semibold px-2 py-1 bg-red-500 text-white rounded whitespace-nowrap" data-testid={`qc-eval-${i}`}>Uygun Değil ✗</span>}
                    </div>
                    {outOfRange && (
                      <div className="col-span-4 text-[10px] text-red-700 font-semibold flex items-center gap-1">
                        ⚠ Ölçüm kabul aralığı dışında: {s.measured} {s.unit || ""} · Referans: {s.lower}–{s.upper}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* Şahit Numune (GMP) — QC onayı öncesi zorunlu teyit */}
        <div className={`flex items-start gap-2 rounded border px-3 py-2 mb-3 ${retainedSample ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"}`} data-testid="qc-retained-sample-block">
          <input
            id="qc-retained-sample"
            type="checkbox"
            checked={retainedSample}
            onChange={e => setRetainedSample(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-emerald-600 cursor-pointer"
            data-testid="qc-retained-sample-checkbox"
          />
          <label htmlFor="qc-retained-sample" className="text-xs cursor-pointer select-none flex-1">
            <span className={`font-semibold ${retainedSample ? "text-emerald-800" : "text-amber-800"}`}>Şahit Numune Alındı (GMP zorunlu)</span>
            <div className="text-[11px] text-slate-600 mt-0.5">Onaylamadan önce şahit numunenin alındığını teyit edin. Aksi halde onay butonu devre dışıdır.</div>
          </label>
        </div>
        <div className="flex gap-2 justify-end items-center">
          {(() => {
            const numeric = (qcForm.specs || []).filter(s => s.type !== "text" && s.type !== "yesno" && s.lower != null && s.upper != null);
            const yesnoFail = (qcForm.specs || []).filter(s => s.type === "yesno" && (s.text_result || "Uygun") === "Uygun Değil");
            if (numeric.length === 0 && yesnoFail.length === 0) return null;
            const evaluated = numeric.filter(s => s.measured != null && !Number.isNaN(s.measured));
            if (evaluated.length === 0 && yesnoFail.length === 0) return null;
            const failing = evaluated.filter(s => s.measured < s.lower || s.measured > s.upper);
            const allPass = failing.length === 0 && yesnoFail.length === 0 && evaluated.length === numeric.length;
            const failCount = failing.length + yesnoFail.length;
            return (
              <div className={`text-xs font-semibold px-3 py-1.5 rounded border mr-auto ${allPass ? "bg-emerald-50 border-emerald-300 text-emerald-800" : failCount > 0 ? "bg-red-50 border-red-300 text-red-800" : "bg-amber-50 border-amber-300 text-amber-800"}`} data-testid="qc-overall-eval">
                Genel Sonuç: {allPass ? "Uygun ✓" : failCount > 0 ? `Uygun Değil ✗ (${failCount} kriter)` : `${evaluated.length}/${numeric.length} tamamlandı`}
              </div>
            );
          })()}
          <Btn variant="secondary" onClick={() => setQcOpen(false)}>Vazgeç</Btn>
          <Btn onClick={async () => { const r = await api.post("/qc", qcForm); await api.post(`/qc/${r.id}/finalize`, { result: "fail" }); setQcOpen(false); load(); }} variant="reject" size="xl">Reddet</Btn>
          <Btn disabled={!retainedSample} title={!retainedSample ? "Önce Şahit Numune kutusunu işaretleyin" : ""} onClick={async () => {
            if (!retainedSample) { alert("Şahit Numune teyit edilmeden onay yapılamaz."); return; }
            // Validate: every spec must have a value entered
            const missing = qcForm.specs.filter(s => {
              if (s.type === "yesno") return !s.text_result;
              if (s.type === "text") return !s.text_result || !s.text_result.toString().trim();
              return s.measured === null || s.measured === undefined || Number.isNaN(s.measured);
            });
            if (missing.length > 0) {
              alert(`Aşağıdaki QC parametreleri boş — değer girmeden onaylayamazsınız:\n\n• ${missing.map(m => m.name || "?").join("\n• ")}`);
              return;
            }
            // iter317 — yesno "Uygun Değil" → otomatik fail
            const yesnoFail = qcForm.specs.filter(s => s.type === "yesno" && s.text_result === "Uygun Değil");
            if (yesnoFail.length > 0) {
              alert(`Aşağıdaki kriter(ler) 'Uygun Değil' seçili — genel sonuç UYGUN DEĞİL olacak:\n\n• ${yesnoFail.map(m => m.name).join("\n• ")}`);
              const r = await api.post("/qc", { ...qcForm, retained_sample_taken: true });
              await api.post(`/qc/${r.id}/finalize`, { result: "fail" });
              setQcOpen(false); load();
              return;
            }
            // Auto-fail if any numeric measurement is out of range
            const outOfRange = qcForm.specs.filter(s => s.type !== "text" && s.type !== "yesno" && s.lower != null && s.upper != null && (s.measured < s.lower || s.measured > s.upper));
            if (outOfRange.length > 0) {
              const proceed = window.confirm(`Aşağıdaki ölçümler kabul aralığı dışında:\n\n• ${outOfRange.map(m => `${m.name}: ${m.measured} (${m.lower}–${m.upper})`).join("\n• ")}\n\nYine de ONAYLAmak istediğinize emin misiniz?`);
              if (!proceed) return;
            }
            const r = await api.post("/qc", { ...qcForm, retained_sample_taken: true });
            await api.post(`/qc/${r.id}/finalize`, { result: "pass" });
            setQcOpen(false); load();
          }} data-testid="qc-approve" variant="approve" size="xl">Onayla</Btn>
        </div>
      </Modal>

      {/* iter316 — Hammadde QC Detay/Düzenle modali */}
      <Modal open={!!qcDetail} onClose={() => { setQcDetail(null); setQcEditing(false); }} title={`Hammadde QC — ${qcDetail?.qc_no || ""}`} size="lg">
        {qcDetail && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 border rounded p-2">
              <div><b>Hammadde:</b> {qcDetail.material_name || "—"} <span className="text-slate-400 font-mono">{qcDetail.material_code || ""}</span></div>
              <div><b>Lot:</b> <span className="font-mono">{qcDetail.lot_code || "—"}</span></div>
              <div><b>Tedarikçi:</b> {qcDetail.supplier || "—"}</div>
              <div><b>PO No:</b> {qcDetail.source_po_no || "—"}</div>
              <div><b>Teslim:</b> {(qcDetail.received_at || "").slice(0, 10)}</div>
              <div><b>QC Tarihi:</b> {(qcDetail.created_at || "").slice(0, 10)}</div>
              <div><b>Miktar:</b> {qcDetail.qty_received || 0} {qcDetail.unit || ""}</div>
              <div><b>Onaylayan:</b> {qcDetail.approved_by_name || "—"}</div>
            </div>
            {!canEditQC && (
              <div className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
                Görüntüleme modu. QC değerlerini sadece Admin veya Sistem Yetkilisi düzenleyebilir.
              </div>
            )}
            {["koku", "gorunum", "label_readability", "packaging_suitability"].map(key => {
              const label = { koku: "Koku", gorunum: "Görünüm", label_readability: "Etiket Okunurluğu", packaging_suitability: "Ambalaj Uygunluğu" }[key];
              const v = qcDetail.raw_criteria[key] || { result: "Uygun" };
              return (
                <div key={key} className="flex items-center gap-3 text-xs">
                  <label className="w-44 font-semibold text-slate-700">{label}</label>
                  <Select value={v.result} disabled={!qcEditing}
                    onChange={e => setQcDetail(d => ({ ...d, raw_criteria: { ...d.raw_criteria, [key]: { result: e.target.value } } }))}
                    data-testid={`qc-crit-${key}`}>
                    <option value="Uygun">Uygun</option>
                    <option value="Uygun Değil">Uygun Değil</option>
                  </Select>
                </div>
              );
            })}
            {["ph", "viscosity"].map(key => {
              const label = key === "ph" ? "pH" : "Viskozite";
              const v = qcDetail.raw_criteria[key] || { value: null, result: "Uygulanmaz" };
              return (
                <div key={key} className="flex items-center gap-3 text-xs">
                  <label className="w-44 font-semibold text-slate-700">{label}</label>
                  <Input type="text" value={v.value ?? ""} disabled={!qcEditing}
                    onChange={e => setQcDetail(d => ({ ...d, raw_criteria: { ...d.raw_criteria, [key]: { ...v, value: e.target.value } } }))}
                    placeholder={key === "ph" ? "6.2" : "4500 cP"} className="w-28" data-testid={`qc-crit-${key}-val`}/>
                  <Select value={v.result} disabled={!qcEditing}
                    onChange={e => setQcDetail(d => ({ ...d, raw_criteria: { ...d.raw_criteria, [key]: { ...v, result: e.target.value } } }))}
                    data-testid={`qc-crit-${key}`}>
                    <option value="Uygulanmaz">Uygulanmaz</option>
                    <option value="Uygun">Uygun</option>
                    <option value="Uygun Değil">Uygun Değil</option>
                  </Select>
                </div>
              );
            })}
            <Textarea placeholder="Açıklama / Not"
              value={qcDetail.criteria_notes || ""}
              disabled={!qcEditing}
              onChange={e => setQcDetail(d => ({ ...d, criteria_notes: e.target.value }))}
              rows={2}
              data-testid="qc-crit-notes"/>
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="text-xs">
                <b>Genel Sonuç:</b>{" "}
                <span className={qcDetail.result === "fail" ? "text-rose-700 font-bold" : "text-emerald-700 font-bold"} data-testid="qc-detail-result">
                  {qcDetail.result === "fail" ? "UYGUN DEĞİL" : "UYGUN"}
                </span>
                {qcDetail.updated_by_name && <span className="ml-3 text-slate-500">Son düzenleyen: {qcDetail.updated_by_name}</span>}
              </div>
              <div className="flex gap-2">
                <Btn size="sm" variant="secondary" onClick={async () => {
                  try { const blob = await api.getBlob(`/qc/${qcDetail.id}/pdf`); openPdfPreview(blob, `qc_${qcDetail.qc_no}.pdf`); } catch (e) { alert("PDF alınamadı"); }
                }}><FileText size={12}/> PDF</Btn>
                {canEditQC && !qcEditing && (
                  <Btn size="sm" onClick={() => setQcEditing(true)} data-testid="qc-edit-btn">Düzenle</Btn>
                )}
                {canEditQC && qcEditing && (
                  <>
                    <Btn size="sm" variant="secondary" onClick={() => setQcEditing(false)}>Vazgeç</Btn>
                    <Btn size="sm" onClick={saveQcCriteria} data-testid="qc-crit-save">Kaydet</Btn>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export function FinishedProductsPage() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(null);
  const [noteOpen, setNoteOpen] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [filteredRows, setFilteredRows] = useState([]);
  const [dform, setDform] = useState({ qty: 0, sales_order_id: "", carrier: "", vehicle: "", tracking_no: "" });
  // Teslimat geçmişi modalı (spec msg-173.1 — Bitmiş Ürünler sayfası aynı zamanda
  // sevkiyat sayfası, ayrı bir Sevkiyat modülü yok).
  const [historyOpen, setHistoryOpen] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  const load = () => Promise.all([api.get("/finished-lots"), api.get("/products"), api.get("/customers"), api.get("/sales-orders")])
    .then(([f, p, c, o]) => { setRows(f); setProducts(p); setCustomers(c); setOrders(o); });
  useEffect(() => { load(); }, []);
  const pname = id => products.find(p => p.id === id)?.name || "—";
  const cname = id => customers.find(c => c.id === id)?.legal_name || "—";
  const soForLot = r => orders.find(o => o.id === r.sales_order_id) || orders.find(o => o.customer_id === r.customer_id && (o.lines || []).some(l => l.product_id === r.product_id));
  const startDeliver = (r) => {
    setOpen(r); setDform({ qty: r.qty_available, sales_order_id: "", carrier: "", vehicle: "", tracking_no: "" });
  };
  const doDeliver = async () => {
    try {
      await api.post("/shipments/quick-deliver", { finished_lot_id: open.id, ...dform, qty: parseInt(dform.qty) });
      setOpen(null); load(); alert("Sevkiyat oluşturuldu");
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const saveNote = async (id) => {
    try { await api.put(`/finished-lots/${id}/note`, { note: noteDraft }); setNoteOpen(null); setNoteDraft(""); load(); }
    catch (e) { alert(e.response?.data?.detail || "Not kaydedilemedi"); }
  };
  const toggleInvoice = async (r) => {
    try { await api.post(`/finished-lots/${r.id}/toggle-invoice`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Fatura durumu değiştirilemedi"); }
  };
  const openHistory = async (r) => {
    setHistoryOpen(r); setHistoryData(null); setHistoryBusy(true);
    try { const d = await api.get(`/finished-lots/${r.id}/deliveries`); setHistoryData(d); }
    catch (e) { alert(e.response?.data?.detail || "Teslimat geçmişi alınamadı"); setHistoryOpen(null); }
    finally { setHistoryBusy(false); }
  };
  const custOrders = orders.filter(o => open && o.customer_id === open.customer_id && o.status !== "completed" && o.status !== "cancelled");

  // Durum: Hazır (sevk=0), Kısmi Sevk (0 < sevk < üretilen), Teslim Edildi (sevk = üretilen)
  const shipStatus = (r) => {
    const produced = Number(r.qty_produced || 0);
    const shipped = Number(r.qty_shipped || 0);
    if (produced > 0 && shipped >= produced) return { key: "delivered", label: "Teslim Edildi", border: "border-l-4 border-emerald-500", badge: "ok", sort: 3 };
    if (shipped > 0) return { key: "partial", label: "Kısmi Sevk", border: "border-l-4 border-orange-400", badge: "attention", sort: 2 };
    return { key: "ready", label: "Hazır", border: "border-l-4 border-yellow-400", badge: "attention", sort: 1 };
  };

  // Durum-farkındalıklı mesaj şablonları (spec msg-173.1 rules 8-15).
  const buildMessage = (r, channel) => {
    const so = soForLot(r);
    const orderNo = so?.order_no || "—";
    const projectNo = so?.project_no || r.project_no || "—";
    const productName = pname(r.product_id);
    const produced = Number(r.qty_produced || 0);
    const shipped = Number(r.qty_shipped || 0);
    const remaining = Math.max(0, produced - shipped);
    const st = shipStatus(r).key;
    const meta = `Sipariş Numarası: ${orderNo}\nProje Numarası: ${projectNo}`;
    if (st === "ready") {
      return {
        subject: `${productName} — Sevkiyata Hazır`,
        text: `Sayın Yetkili,\n\n${productName} ürününüze ait ${produced} adet ürün sevkiyata hazırdır.\n\n${meta}\n\nTeslimat planlaması için tarafımıza bilgi vermenizi rica ederiz.\n\nİyi çalışmalar dileriz.\nMeta Pharma Kozmetik`,
      };
    }
    if (st === "partial") {
      const lastDate = new Date().toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" });
      return {
        subject: `${productName} — Kısmi Sevkiyat Bildirimi`,
        text: `Sayın Yetkili,\n\n${productName} ürününüze ait ${lastDate} tarihinde ${shipped} adet ürün teslim edilmiştir.\n\n${meta}\nKalan teslimat miktarı: ${remaining} adet.\n\nKalan ürünlerinizin teslimat planlaması için tarafımıza bilgi vermenizi rica ederiz.\n\nİyi çalışmalar dileriz.\nMeta Pharma Kozmetik`,
      };
    }
    // delivered
    return {
      subject: `${productName} — Teslimat Tamamlandı`,
      text: `Sayın Yetkili,\n\n${productName} ürününüze ait toplam ${produced} adet ürünün teslimatı tamamlanmıştır.\n\n${meta}\n\nÜrünlerinizin teslimatı tamamlanmıştır.\n\nİyi çalışmalar dileriz.\nMeta Pharma Kozmetik`,
    };
  };

  // Aynı üretim lotunu (prod_no / lot_code) tek satırda göstermek için grupla, sonra sırala
  const grouped = React.useMemo(() => {
    const bucket = new Map();
    for (const r of rows) {
      const key = r.lot_code || r.production_order_id || r.id;
      if (!bucket.has(key)) bucket.set(key, { ...r, _members: [r] });
      else {
        const g = bucket.get(key);
        g.qty_produced = (g.qty_produced || 0) + (r.qty_produced || 0);
        g.qty_available = (g.qty_available || 0) + (r.qty_available || 0);
        g.qty_shipped = (g.qty_shipped || 0) + (r.qty_shipped || 0);
        g._members.push(r);
      }
    }
    const arr = Array.from(bucket.values());
    arr.sort((a, b) => shipStatus(a).sort - shipStatus(b).sort);
    return arr;
  }, [rows]);

  const displayed = filteredRows.length > 0 || rows.length === 0 ? filteredRows : grouped;

  return (
    <div>
      <PageHeader title="Bitmiş Ürünler & Sevkiyat" description="Üretim lotları ve sevkiyat durumu — sarı: hazır · turuncu: kısmi sevk · yeşil: teslim edildi" />
      <FilterAndReportBar
        reportType="finished_products" rows={grouped} customers={customers}
        statuses={[{value:"ready",label:"Hazır"},{value:"partial",label:"Kısmi Sevk"},{value:"delivered",label:"Teslim Edildi"}]}
        dateAccessor={r => r.created_at || r.mfg_date}
        customerAccessor={r => r.customer_id}
        productAccessor={r => products.find(p => p.id === r.product_id)?.name || ""}
        statusAccessor={r => shipStatus(r).key}
        onFiltered={setFilteredRows}
      />
      <Table testid="fp-table" rows={displayed} columns={[
        { label: "Durum", render: r => {
          const st = shipStatus(r);
          return <div className={`inline-flex items-center pl-2 py-0.5 ${st.border}`} data-testid={`fp-status-${r.id}`}>
            <Badge level={st.badge}>{st.label}</Badge>
          </div>;
        } },
        { label: "Üretim Lotu", render: r => <span className="font-mono text-xs">{r.lot_code}</span> },
        { label: "Ürün", render: r => pname(r.product_id) },
        { label: "Müşteri", render: r => cname(r.customer_id) },
        { label: "SKT", render: r => (r.expiry_date || "").slice(0, 10) },
        { label: "Üretildi", render: r => <span className="font-mono">{r.qty_produced}</span> },
        { label: "Stok", render: r => <span className="font-mono">{r.qty_available}</span> },
        { label: "Sevk", render: r => <span className="font-mono">{r.qty_shipped || 0}</span> },
        { label: "Fatura", render: r => (
          <button onClick={e => { e.stopPropagation(); toggleInvoice(r); }} data-testid={`fp-invoice-${r.id}`} className="cursor-pointer">
            <Badge level={r.invoice_status === "invoiced" ? "ok" : "inactive"}>{r.invoice_status === "invoiced" ? "Kesildi ✓" : "Kesilmedi"}</Badge>
          </button>
        ) },
        { label: "Not", render: r => (
          <div onClick={e => e.stopPropagation()} className="flex items-center gap-1">
            {r.note && <span className="text-[10px] text-slate-500 max-w-[120px] truncate italic" title={r.note}>{r.note}</span>}
            <Btn size="sm" variant="secondary" onClick={() => { setNoteOpen(r.id); setNoteDraft(r.note || ""); }} data-testid={`fp-note-btn-${r.id}`}>
              <FileText size={12}/> {r.note ? "Görüntüle" : "Ekle"}
            </Btn>
          </div>
        ) },
        { label: "İşlem", render: r => {
          return (
          <div className="flex gap-1 flex-wrap" onClick={e => e.stopPropagation()}>
            <Btn size="sm" variant="secondary" onClick={async () => {
              try {
                const token = localStorage.getItem("erp_token");
                const absOrigin = window.location.origin;
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/finished-lots/${r.id}/label?base_url=${encodeURIComponent(absOrigin)}`;
                const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) { const errTxt = await res.text(); alert(`Etiket alınamadı (${res.status}): ${errTxt.slice(0, 200)}`); return; }
                const html = await res.text();
                const blob = new Blob([html], { type: "text/html" });
                const blobUrl = URL.createObjectURL(blob);
                const w = window.open(blobUrl, "_blank");
                if (!w) { alert("Yeni sekme açılamadı. Popup engelleyiciyi kapatın."); URL.revokeObjectURL(blobUrl); return; }
                setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
              } catch (e) { alert(`Etiket açılamadı: ${e.message || e}`); }
            }} data-testid={`print-label-${r.id}`}><Printer size={12}/> Etiket</Btn>
            {(() => { const msg = buildMessage(r, "tg"); const st = shipStatus(r).key;
              const label = st === "ready" ? "Hazır Bilgisi" : st === "partial" ? "Kısmi Sevk Bilgisi" : "Teslim Bilgisi";
              return <>
                <TelegramSendButton
                  customer_id={r.customer_id}
                  kind={`fp_${st}`}
                  entity_ref={r.lot_code}
                  label={label}
                  defaultMessage={msg.text}
                  testid={`tg-fp-${r.id}`}
                />
                <EmailSendButton
                  customer_id={r.customer_id}
                  kind={`fp_${st}`}
                  entity_ref={r.lot_code}
                  label={label}
                  defaultSubject={msg.subject}
                  defaultMessage={msg.text}
                  testid={`email-fp-${r.id}`}
                />
              </>;
            })()}
            <Btn size="sm" variant="secondary" onClick={() => openHistory(r)} data-testid={`fp-history-${r.id}`}><History size={12}/> Teslimat Geçmişi</Btn>
            {r.status === "approved" && r.qty_available > 0 &&
              <Btn size="sm" onClick={() => startDeliver(r)} data-testid={`deliver-${r.id}`}><Truck size={12}/> Teslim / Sevk</Btn>}
          </div>
        );} },
      ]} />
      <Modal open={!!open} onClose={() => setOpen(null)} title={`Teslim / Sevkiyat — ${open?.lot_code || ""}`}>
        <div className="text-sm mb-3 text-slate-600">Mevcut stok: <span className="font-mono font-semibold">{open?.qty_available}</span> adet</div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Teslim Miktarı *" type="number" value={dform.qty} onChange={e => setDform({ ...dform, qty: e.target.value })} data-testid="deliver-qty"/>
          <Select label="Satış Siparişi (opsiyonel)" value={dform.sales_order_id} onChange={e => setDform({ ...dform, sales_order_id: e.target.value })}>
            <option value="">Otomatik eşle</option>
            {custOrders.map(o => <option key={o.id} value={o.id}>{o.order_no}</option>)}
          </Select>
          <Input label="Taşıyıcı" value={dform.carrier} onChange={e => setDform({ ...dform, carrier: e.target.value })} />
          <Input label="Araç Plakası" value={dform.vehicle} onChange={e => setDform({ ...dform, vehicle: e.target.value })} />
          <Input label="Takip No" value={dform.tracking_no} onChange={e => setDform({ ...dform, tracking_no: e.target.value })} />
        </div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(null)}>Vazgeç</Btn><Btn onClick={doDeliver} data-testid="do-deliver">Sevk Et</Btn></div>
      </Modal>
      <Modal open={!!historyOpen} onClose={() => { setHistoryOpen(null); setHistoryData(null); }} title={`Teslimat Geçmişi — ${historyOpen?.lot_code || ""}`} size="lg">
        {historyBusy && <div className="text-center py-6 text-slate-500 text-sm">Yükleniyor…</div>}
        {!historyBusy && historyData && (
          <div className="space-y-4" data-testid="fp-history-panel">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded p-2">
                <div className="uppercase text-[10px] text-slate-500">Toplam Sipariş / Üretilen</div>
                <div className="font-mono font-semibold text-sm">{historyData.qty_produced}</div>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-500/30 rounded p-2">
                <div className="uppercase text-[10px] text-emerald-700 dark:text-emerald-300">Teslim Edilen</div>
                <div className="font-mono font-semibold text-sm">{historyData.qty_shipped}</div>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/30 rounded p-2">
                <div className="uppercase text-[10px] text-amber-700 dark:text-amber-300">Kalan</div>
                <div className="font-mono font-semibold text-sm">{historyData.qty_remaining}</div>
              </div>
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-500/30 rounded p-2">
                <div className="uppercase text-[10px] text-indigo-700 dark:text-indigo-300">Durum</div>
                <div className="font-semibold text-sm">
                  {historyData.computed_status === "ready" ? "Hazır" : historyData.computed_status === "partial" ? "Kısmi Sevk" : "Teslim Edildi"}
                </div>
              </div>
            </div>
            <div>
              <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Truck size={14}/> Teslimat Hareketleri</div>
              {(historyData.deliveries || []).length === 0 ? (
                <div className="text-xs text-slate-400 italic py-4 text-center">Henüz teslimat yapılmamış.</div>
              ) : (
                <table className="w-full text-xs" data-testid="fp-history-table">
                  <thead className="text-[10px] uppercase text-slate-500 border-b">
                    <tr>
                      <th className="text-left py-1">Tarih</th>
                      <th className="text-left">Sevkiyat No</th>
                      <th className="text-right">Teslim Edilen</th>
                      <th className="text-right">Kalan (Öncesi)</th>
                      <th className="text-right">Kalan (Sonrası)</th>
                      <th className="text-left">Yapan</th>
                      <th className="text-left">Taşıyıcı</th>
                      <th className="text-left">Fatura</th>
                      <th className="text-left">İrsaliye</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.deliveries.map((d, i) => (
                      <tr key={i} className="border-b border-slate-100 dark:border-slate-800" data-testid={`fp-history-row-${i}`}>
                        <td className="py-1.5 font-mono">{(d.delivery_date || "").slice(0, 10)}</td>
                        <td className="font-mono text-xs">{d.shipment_no}</td>
                        <td className="text-right font-mono font-semibold text-emerald-700 dark:text-emerald-400">{d.delivered_qty}</td>
                        <td className="text-right font-mono text-slate-500">{d.remaining_before}</td>
                        <td className="text-right font-mono">{d.remaining_after}</td>
                        <td className="text-slate-600 dark:text-slate-300">{d.user_name || "—"}</td>
                        <td className="text-slate-600 dark:text-slate-300">{d.carrier || "—"}{d.tracking_no ? ` · ${d.tracking_no}` : ""}</td>
                        <td>{d.invoice_status === "invoiced" ? <Badge level="ok">Kesildi</Badge> : <Badge level="inactive">Kesilmedi</Badge>}</td>
                        <td>
                          <button
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem("erp_token");
                                const url = `${process.env.REACT_APP_BACKEND_URL}/api/shipments/${d.shipment_id}/irsaliye.pdf`;
                                const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                                if (!res.ok) { alert(`İrsaliye alınamadı (${res.status})`); return; }
                                const blob = await res.blob();
                                openPdfPreview(blob, `Irsaliye-${d.shipment_no}.pdf`);
                              } catch (e) { alert(`İrsaliye açılamadı: ${e.message || e}`); }
                            }}
                            className="text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                            data-testid={`irsaliye-${d.shipment_id}`}
                            title="İrsaliye PDF indir"
                          >
                            <Printer size={11}/> İrsaliye
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div>
              <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Mail size={14}/> İletişim Kayıtları</div>
              {(historyData.communications || []).length === 0 ? (
                <div className="text-xs text-slate-400 italic py-3 text-center">İletişim kaydı yok.</div>
              ) : (
                <table className="w-full text-xs" data-testid="fp-history-comms">
                  <thead className="text-[10px] uppercase text-slate-500 border-b">
                    <tr>
                      <th className="text-left py-1">Tarih</th>
                      <th className="text-left">Kanal</th>
                      <th className="text-left">Alıcı</th>
                      <th className="text-left">Durum</th>
                      <th className="text-left">Gönderen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.communications.map((c, i) => (
                      <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                        <td className="py-1.5 font-mono">{(c.sent_at || "").slice(0, 16).replace("T", " ")}</td>
                        <td>
                          <Badge level={c.channel === "EMAIL" ? "attention" : "ok"}>{c.channel}</Badge>
                        </td>
                        <td className="font-mono text-[10px]">{c.recipient_address}</td>
                        <td>
                          <Badge level={c.delivery_status === "SENT" ? "ok" : "missing"}>{c.delivery_status}</Badge>
                          {c.error_message && <span className="text-red-600 text-[10px] ml-1" title={c.error_message}>⚠</span>}
                        </td>
                        <td className="text-slate-600 dark:text-slate-300">{c.sent_by_name || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
        <div className="flex justify-end mt-4"><Btn variant="secondary" onClick={() => { setHistoryOpen(null); setHistoryData(null); }}>Kapat</Btn></div>
      </Modal>
      <Modal open={!!noteOpen} onClose={() => { setNoteOpen(null); setNoteDraft(""); }} title="Not Ekle / Görüntüle">
        <Textarea label="Not" value={noteDraft} onChange={e => setNoteDraft(e.target.value)} rows={5} data-testid="fp-note-input" placeholder="Sevkiyat, müşteri iletişimi veya lot ile ilgili not..." />
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => { setNoteOpen(null); setNoteDraft(""); }}>Vazgeç</Btn>
          <Btn onClick={() => saveNote(noteOpen)} data-testid="fp-note-save">Kaydet</Btn>
        </div>
      </Modal>
    </div>
  );
}

export function ShipmentsPage() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [fps, setFps] = useState([]);
  const [open, setOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [filteredRows, setFilteredRows] = useState([]);
  const [f, setF] = useState({ customer_id: "", carrier: "", vehicle: "", tracking_no: "", lines: [] });
  const load = () => Promise.all([api.get("/shipments"), api.get("/customers"), api.get("/sales-orders"), api.get("/finished-lots", { status: "approved" })])
    .then(([s, c, o, fp]) => { setRows(s); setCustomers(c); setOrders(o); setFps(fp); });
  useEffect(() => { load(); }, []);
  const save = async () => { try { await api.post("/shipments", f); setOpen(false); setF({ customer_id: "", carrier: "", vehicle: "", tracking_no: "", lines: [] }); load(); } catch (e) { alert(e.response?.data?.detail); } };
  const saveNote = async (id) => { try { await api.put(`/shipments/${id}/delivery-note`, { delivery_note: noteDraft }); setNoteOpen(null); load(); } catch (e) { alert(e.response?.data?.detail || "Not kaydedilemedi"); } };

  const custOrders = orders.filter(o => !f.customer_id || o.customer_id === f.customer_id);
  const cname = id => customers.find(c => c.id === id)?.legal_name || "—";
  const addLine = () => setF({ ...f, lines: [...f.lines, { sales_order_id: "", sales_order_line_index: 0, product_id: "", finished_lot_id: "", qty: 0 }] });
  const updLine = (i, k, v) => { const l = [...f.lines]; l[i] = { ...l[i], [k]: v }; setF({ ...f, lines: l }); };

  return (
    <div>
      <PageHeader title="Sevkiyatlar" description="Sevkiyat kayıtları ve teslimat notları" actions={<Btn onClick={() => setOpen(true)} data-testid="new-shipment"><Plus size={14}/> Yeni Sevkiyat</Btn>} />
      <FilterAndReportBar
        reportType="shipments" rows={rows} customers={customers}
        statuses={[{value:"prepared",label:"Hazırlanıyor"},{value:"shipped",label:"Yolda"},{value:"delivered",label:"Teslim Edildi"}]}
        dateAccessor={r => r.created_at}
        customerAccessor={r => r.customer_id}
        productAccessor={r => (r.lines||[]).map(ln => ln.product_name || "").join(" ")}
        statusAccessor={r => r.status}
        onFiltered={setFilteredRows}
      />
      <Table testid="ship-table" rows={filteredRows.length > 0 || rows.length === 0 ? filteredRows : rows} columns={[
        { label: "Sevkiyat", render: r => <span className="font-mono text-xs">{r.shipment_no}</span> },
        { label: "Müşteri", render: r => cname(r.customer_id) },
        { label: "Taşıyıcı", key: "carrier" },
        { label: "Kalem", render: r => r.lines?.length || 0 },
        { label: "Durum", render: r => <Badge level={r.status === "delivered" ? "ok" : r.status === "shipped" ? "attention" : "inactive"}>{r.status === "delivered" ? "Teslim Edildi" : r.status === "shipped" ? "Yolda" : r.status === "prepared" ? "Hazırlanıyor" : r.status}</Badge> },
        { label: "Fatura", render: r => (
          <button onClick={async (e) => { e.stopPropagation(); if (r.status === "shipped" || r.status === "delivered") { try { await api.post(`/shipments/${r.id}/toggle-invoice`); load(); } catch (e2) { alert(e2.response?.data?.detail); } } }} data-testid={`toggle-inv-${r.id}`}>
            <Badge level={r.invoice_status === "invoiced" ? "ok" : "attention"}>{r.invoice_status === "invoiced" ? "Kesildi ✓" : "Kesilmedi"}</Badge>
          </button>
        ) },
        { label: "Teslimat Notu", render: r => {
          const isEditing = noteOpen === r.id;
          if (isEditing) {
            return (
              <div className="flex flex-col gap-1 min-w-[280px]" onClick={e => e.stopPropagation()}>
                <textarea value={noteDraft} onChange={e => setNoteDraft(e.target.value)}
                  className="w-full text-xs px-2 py-1 border border-indigo-300 dark:border-indigo-500 rounded bg-white dark:bg-slate-800 dark:text-slate-100" rows={3}
                  placeholder="Teslimat notu yazın..." data-testid={`ship-note-input-${r.id}`}/>
                <div className="flex gap-1 justify-end">
                  <Btn size="sm" variant="secondary" onClick={() => setNoteOpen(null)}>İptal</Btn>
                  <Btn size="sm" onClick={() => saveNote(r.id)} data-testid={`ship-note-save-${r.id}`}>Kaydet</Btn>
                </div>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-2 min-w-[240px] max-w-[380px]" onClick={e => e.stopPropagation()}>
              <span className="text-xs text-slate-700 dark:text-slate-200 flex-1 whitespace-normal">{r.delivery_note ? r.delivery_note : <span className="text-slate-400 italic">Not eklenmemiş</span>}</span>
              <Btn size="sm" variant="secondary" onClick={() => { setNoteOpen(r.id); setNoteDraft(r.delivery_note || ""); }} data-testid={`ship-note-edit-${r.id}`}>
                <FileText size={12}/> {r.delivery_note ? "Düzenle" : "Ekle"}
              </Btn>
            </div>
          );
        } },
      ]} />

      <Modal open={open} onClose={() => setOpen(false)} title="Yeni Sevkiyat" size="full">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <Select label="Müşteri *" value={f.customer_id} onChange={e => setF({ ...f, customer_id: e.target.value })} data-testid="ship-cust">
            <option value="">Seçiniz...</option>{customers.map(c => <option key={c.id} value={c.id}>{c.legal_name}</option>)}
          </Select>
          <Input label="Taşıyıcı" value={f.carrier} onChange={e => setF({ ...f, carrier: e.target.value })} />
          <Input label="Araç Plaka" value={f.vehicle} onChange={e => setF({ ...f, vehicle: e.target.value })} />
        </div>
        <div className="text-sm font-semibold mb-2">Kalemler</div>
        {f.lines.map((l, i) => {
          const order = custOrders.find(o => o.id === l.sales_order_id);
          const custFps = fps.filter(fp => (!l.product_id || fp.product_id === l.product_id) && fp.customer_id === f.customer_id);
          return (
            <div key={i} className="grid grid-cols-12 gap-2 mb-2 bg-white border rounded p-2">
              <select value={l.sales_order_id} onChange={e => updLine(i, "sales_order_id", e.target.value)} className="col-span-4 px-2 py-1 text-xs border rounded">
                <option value="">Sipariş seç...</option>
                {custOrders.map(o => <option key={o.id} value={o.id}>{o.order_no}</option>)}
              </select>
              <select value={l.sales_order_line_index} onChange={e => { const idx = parseInt(e.target.value); updLine(i, "sales_order_line_index", idx); if (order) updLine(i, "product_id", order.lines[idx]?.product_id); }} className="col-span-2 px-2 py-1 text-xs border rounded">
                {order?.lines.map((ln, idx) => <option key={idx} value={idx}>Kalem #{idx + 1}</option>)}
              </select>
              <select value={l.finished_lot_id} onChange={e => updLine(i, "finished_lot_id", e.target.value)} className="col-span-4 px-2 py-1 text-xs border rounded" data-testid={`ship-fp-${i}`}>
                <option value="">Bitmiş lot seç...</option>
                {custFps.map(fp => <option key={fp.id} value={fp.id}>{fp.lot_code} (stok: {fp.qty_available})</option>)}
              </select>
              <input type="number" value={l.qty} onChange={e => updLine(i, "qty", parseInt(e.target.value) || 0)} placeholder="Adet" className="col-span-2 px-2 py-1 text-xs border rounded" data-testid={`ship-qty-${i}`}/>
            </div>
          );
        })}
        <Btn size="sm" variant="secondary" onClick={addLine} data-testid="add-ship-line"><Plus size={12}/> Kalem Ekle</Btn>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn><Btn onClick={save} data-testid="save-shipment">Kaydet</Btn></div>
      </Modal>
    </div>
  );
}

export function TraceabilityPage() {
  const [mode, setMode] = useState("finished");
  const [id, setId] = useState("");
  const [result, setResult] = useState(null);
  const trace = async () => {
    if (!id) return;
    try {
      const r = mode === "finished" ? await api.get(`/traceability/finished-lot/${id}`) : await api.get(`/traceability/raw-lot/${id}`);
      setResult(r);
    } catch (e) { alert("Lot bulunamadı"); }
  };
  return (
    <div>
      <PageHeader title="İzlenebilirlik" description="İleri ve geri lot izleme, recall yönetimi" />
      <Card className="p-4 mb-4">
        <div className="flex gap-2 items-end">
          <Select label="İzleme Yönü" value={mode} onChange={e => setMode(e.target.value)}>
            <option value="finished">İleri (Bitmiş → Ham madde)</option>
            <option value="raw">Geri (Ham madde → Bitmiş)</option>
          </Select>
          <Input label="Lot ID" value={id} onChange={e => setId(e.target.value)} data-testid="trace-lot-id"/>
          <Btn onClick={trace} data-testid="do-trace"><Route size={14}/> İzle</Btn>
        </div>
      </Card>
      {result && (
        <pre className="bg-slate-900 text-slate-100 text-xs p-4 rounded-lg overflow-auto max-h-[600px] font-mono">{JSON.stringify(result, null, 2)}</pre>
      )}
    </div>
  );
}

/* ============================================================
   iter123 — QC Test Takip / Firma İçi Testler
   Uses mevcut R&D projects/samples (FK), central files, PDF theme.
============================================================ */
const STATUS_BADGE = {
  "BEKLİYOR":        "bg-amber-100 text-amber-800 border-amber-300",
  "TEST YAPILIYOR":  "bg-indigo-100 text-indigo-800 border-indigo-300",
  "TAMAMLANDI":      "bg-emerald-100 text-emerald-800 border-emerald-300",
  "GECİKTİ":         "bg-rose-100 text-rose-800 border-rose-300",
};
const RESULT_LABEL = { uygun: "Uygun", uygun_degil: "Uygun Değil", degerlendirilemedi: "Değerlendirilemedi" };
const RESULT_BADGE = {
  uygun: "bg-emerald-100 text-emerald-800 border-emerald-300",
  uygun_degil: "bg-rose-100 text-rose-800 border-rose-300",
  degerlendirilemedi: "bg-slate-100 text-slate-600 border-slate-300",
};

function QcTestTracking({ internalOnly }) {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [newOpen, setNewOpen] = useState(false);
  const [detailFor, setDetailFor] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = internalOnly ? { is_internal: true } : {};
      const [r, t] = await Promise.all([
        api.get("/qc-tests", params),
        api.get("/qc-tests/types"),
      ]);
      setRows(r || []); setTypes(t || []);
    } catch (e) { console.error(e); } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [internalOnly]);

  const filtered = useMemo(() => {
    let arr = [...rows];
    if (statusFilter !== "all") arr = arr.filter(r => r.status === statusFilter);
    if (typeFilter !== "all") arr = arr.filter(r => r.test_type === typeFilter);
    if (q.trim()) {
      const qt = q.toLocaleLowerCase("tr");
      arr = arr.filter(r =>
        (`${r.project_no_snapshot} ${r.product_name_snapshot} ${r.customer_name_snapshot} ${r.lab_name}`)
          .toLocaleLowerCase("tr").includes(qt));
    }
    return arr;
  }, [rows, statusFilter, typeFilter, q]);

  return (
    <div data-testid={`qc-tracking-${internalOnly ? "internal" : "all"}`}>
      <div className="mb-3 flex flex-wrap gap-2 items-end bg-slate-50/50 border border-slate-200 rounded p-3">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase text-slate-500 font-semibold">Arama</label>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Proje / ürün / müşteri / lab"
            className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded" data-testid="qc-search"/>
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-500 font-semibold block">Durum</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-2 py-1.5 text-sm border border-slate-300 rounded" data-testid="qc-filter-status">
            <option value="all">Tümü</option>
            {Object.keys(STATUS_BADGE).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-500 font-semibold block">Tür</label>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            className="px-2 py-1.5 text-sm border border-slate-300 rounded" data-testid="qc-filter-type">
            <option value="all">Tümü</option>
            {types.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <Btn onClick={() => setNewOpen(true)} className="bg-indigo-600 text-white" data-testid="qc-new-test">
          <Plus size={14}/> Test Oluştur
        </Btn>
      </div>

      <div className="bg-white border border-slate-200 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-2 py-2">Proje No</th>
              <th className="text-left px-2 py-2">Ürün / Müşteri</th>
              <th className="text-left px-2 py-2">Test / Lab</th>
              <th className="text-left px-2 py-2">Başlama / Bitiş</th>
              <th className="text-center px-2 py-2">Sonuç</th>
              <th className="text-center px-2 py-2">Durum</th>
              <th className="text-center px-2 py-2">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">{busy ? "Yükleniyor..." : "Kayıt yok"}</td></tr>}
            {filtered.map(r => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`qc-row-${r.id}`}>
                <td className="px-2 py-1.5 font-mono text-xs">{r.project_no_snapshot} <span className="text-slate-400">· {r.revision_snapshot}</span></td>
                <td className="px-2 py-1.5">
                  <div className="font-semibold text-slate-800 text-xs">{r.product_name_snapshot}</div>
                  <div className="text-[10px] text-slate-500">{r.customer_name_snapshot}</div>
                </td>
                <td className="px-2 py-1.5">
                  <div className="font-semibold text-xs">{r.test_type_label} {r.is_internal ? <Badge level="info">İç</Badge> : <Badge level="warn">Dış</Badge>}</div>
                  <div className="text-[10px] text-slate-500">{r.lab_name}</div>
                </td>
                <td className="px-2 py-1.5 text-xs font-mono">
                  <div>{r.start_date || r.sent_date || "—"}</div>
                  <div className="text-slate-500">{r.actual_end_date || r.expected_end_date || "—"}</div>
                </td>
                <td className="px-2 py-1.5 text-center">
                  {r.result ? <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-bold ${RESULT_BADGE[r.result]}`}>{RESULT_LABEL[r.result]}</span> : "—"}
                </td>
                <td className="px-2 py-1.5 text-center">
                  <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-bold ${STATUS_BADGE[r.status]}`} data-testid={`qc-status-${r.id}`}>{r.status}</span>
                </td>
                <td className="px-2 py-1.5 text-center">
                  <button onClick={() => setDetailFor(r)} className="text-indigo-700 hover:underline text-xs" data-testid={`qc-open-${r.id}`}>Aç</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {newOpen && <NewQcTestModal types={types} onClose={() => setNewOpen(false)} onSaved={() => { setNewOpen(false); load(); }}/>}
      {detailFor && <QcTestDetailModal testId={detailFor.id} onClose={() => setDetailFor(null)} onChanged={load}/>}
    </div>
  );
}

function NewQcTestModal({ types, onClose, onSaved }) {
  const [samples, setSamples] = useState([]);
  const [sampleId, setSampleId] = useState("");
  const [selected, setSelected] = useState({});   // {test_key: {is_internal, lab_name, sent_date, custom_name}}
  const [purpose, setPurpose] = useState("");
  const [sampleLot, setSampleLot] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/qc-tests/available-samples").then(setSamples).catch(() => setSamples([])); }, []);
  const sample = samples.find(s => s.id === sampleId);

  const toggle = (key) => setSelected(prev => {
    const clone = { ...prev };
    if (clone[key]) delete clone[key];
    else clone[key] = { is_internal: false, lab_name: "", sent_date: "", custom_name: "" };
    return clone;
  });
  const upd = (key, patch) => setSelected(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const expectedEnd = (key, sent) => {
    const meta = types.find(t => t.key === key);
    if (!meta || !sent || !meta.external_days) return "";
    const d = new Date(sent); d.setDate(d.getDate() + meta.external_days);
    return d.toISOString().slice(0, 10);
  };

  const save = async () => {
    if (!sampleId) { setErr("Ar-Ge Projesi seçin"); return; }
    if (Object.keys(selected).length === 0) { setErr("En az bir test türü seçin"); return; }
    setBusy(true); setErr("");
    try {
      for (const [key, cfg] of Object.entries(selected)) {
        const meta = types.find(t => t.key === key);
        if (cfg.is_internal && meta && !meta.internal_supported) { setErr(`${meta.label} firma içi yapılamaz`); setBusy(false); return; }
        if (!cfg.is_internal && !cfg.lab_name.trim()) { setErr(`${meta.label}: laboratuvar adı zorunlu`); setBusy(false); return; }
        if (!cfg.is_internal && !cfg.sent_date) { setErr(`${meta.label}: gönderim tarihi zorunlu`); setBusy(false); return; }
        if (key === "diger" && !cfg.custom_name.trim()) { setErr("'Diğer' için test adı zorunlu"); setBusy(false); return; }
        await api.post("/qc-tests", {
          rd_sample_id: sampleId,
          test_type: key,
          test_type_custom: key === "diger" ? cfg.custom_name.trim() : null,
          is_internal: cfg.is_internal,
          lab_name: cfg.is_internal ? null : cfg.lab_name.trim(),
          sent_date: cfg.is_internal ? null : cfg.sent_date,
          sample_lot: sampleLot || null,
          purpose: purpose || null,
          notes: notes || null,
        });
      }
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || e?.message || "Kayıt başarısız"); setBusy(false); }
  };

  return (
    <Modal open={true} onClose={onClose} title="Yeni Test Oluştur" size="lg">
      <div className="space-y-3" data-testid="qc-new-modal">
        <div>
          <label className="text-[10px] uppercase text-slate-500 font-semibold block">Ar-Ge Projesi *</label>
          <input list="qc-samples" value={sample ? `${sample.project_no} · ${sample.product_name}` : ""}
            onChange={e => { const s = samples.find(x => `${x.project_no} · ${x.product_name}` === e.target.value); setSampleId(s?.id || ""); }}
            placeholder="Proje seçin..." className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded" data-testid="qc-new-sample"/>
          <datalist id="qc-samples">{samples.map(s => <option key={s.id} value={`${s.project_no} · ${s.product_name}`}/>)}</datalist>
          {sample && (
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] bg-indigo-50 border border-indigo-200 rounded p-2" data-testid="qc-new-project-info">
              <div><b>Proje:</b> {sample.project_no}</div>
              <div><b>Revizyon:</b> {sample.revision}</div>
              <div><b>Ürün:</b> {sample.product_name}</div>
              <div><b>Müşteri:</b> {sample.customer_name}</div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Input label="Numune / Parti No" value={sampleLot} onChange={e => setSampleLot(e.target.value)} data-testid="qc-new-lot"/>
          <Input label="Testin Amacı" value={purpose} onChange={e => setPurpose(e.target.value)}/>
        </div>

        <div>
          <label className="text-[10px] uppercase text-slate-500 font-semibold block mb-1">Test Türleri *</label>
          <div className="grid grid-cols-2 gap-2">
            {types.map(t => {
              const cfg = selected[t.key];
              return (
                <div key={t.key} className={`border rounded p-2 ${cfg ? "border-indigo-400 bg-indigo-50/40" : "border-slate-200"}`}>
                  <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold">
                    <input type="checkbox" checked={!!cfg} onChange={() => toggle(t.key)} data-testid={`qc-type-${t.key}`}/>
                    {t.label} {t.external_days ? <span className="text-[10px] text-slate-500">(+{t.external_days}g dış)</span> : null}
                    {t.internal_supported && <span className="text-[10px] text-emerald-700">· iç destekli</span>}
                  </label>
                  {cfg && (
                    <div className="mt-2 space-y-1 pl-5">
                      {t.key === "diger" && (
                        <input value={cfg.custom_name} onChange={e => upd(t.key, { custom_name: e.target.value })}
                          placeholder="Test adı (ör. Dermatolojik)" className="w-full px-1.5 py-1 text-xs border border-slate-300 rounded" data-testid={`qc-custom-${t.key}`}/>
                      )}
                      <div className="flex gap-2 text-xs">
                        <label className="flex items-center gap-1"><input type="radio" checked={!cfg.is_internal} onChange={() => upd(t.key, { is_internal: false })}/> Firma Dışı</label>
                        <label className="flex items-center gap-1">
                          <input type="radio" checked={cfg.is_internal} onChange={() => upd(t.key, { is_internal: true })} disabled={!t.internal_supported}
                            data-testid={`qc-internal-${t.key}`}/>
                          Firma İçi{!t.internal_supported && <span className="text-slate-400"> (yok)</span>}
                        </label>
                      </div>
                      {!cfg.is_internal && (
                        <>
                          <input value={cfg.lab_name} onChange={e => upd(t.key, { lab_name: e.target.value })} placeholder="Laboratuvar / Firma adı"
                            className="w-full px-1.5 py-1 text-xs border border-slate-300 rounded" data-testid={`qc-lab-${t.key}`}/>
                          <div className="flex gap-1 items-center">
                            <input type="date" value={cfg.sent_date} onChange={e => upd(t.key, { sent_date: e.target.value })}
                              className="px-1.5 py-1 text-xs border border-slate-300 rounded" data-testid={`qc-sent-${t.key}`}/>
                            {cfg.sent_date && <span className="text-[10px] text-slate-500">→ Bitiş: <b className="font-mono">{expectedEnd(t.key, cfg.sent_date)}</b></span>}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <Textarea label="Notlar" value={notes} onChange={e => setNotes(e.target.value)} rows={2}/>
        {err && <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded p-2" data-testid="qc-new-err">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
          <Btn onClick={save} disabled={busy || !sampleId} className="bg-indigo-600 text-white" data-testid="qc-new-save">
            {busy ? "Kaydediliyor..." : "Test Oluştur"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

function QcTestDetailModal({ testId, onClose, onChanged }) {
  const [test, setTest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [meas, setMeas] = useState({});   // {"day|cond|field": {value, evaluation}}
  const [uploading, setUploading] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [completeResult, setCompleteResult] = useState("uygun");
  const [completeNotes, setCompleteNotes] = useState("");
  const [specOpen, setSpecOpen] = useState(false);
  const [autoInfo, setAutoInfo] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const t = await api.get(`/qc-tests/${testId}`);
      setTest(t);
      const m = {};
      (t.measurements || []).forEach(r => { m[`${r.day}|${r.condition}|${r.field}`] = { value: r.value, evaluation: r.evaluation }; });
      setMeas(m);
    } catch (e) { console.error(e); } finally { setBusy(false); }
  };
  useEffect(() => { if (testId) load(); /* eslint-disable-next-line */ }, [testId]);

  if (!test) return <Modal open={true} onClose={onClose} title="Test Detayı" size="xl"><div className="text-center text-slate-500 py-8">Yükleniyor...</div></Modal>;

  const isStability = test.is_internal && test.test_type === "stabilite";
  const DAYS = [1, 28, 45, 90];
  const CONDS = [["room", "Oda Sıcaklığı"], ["plus4", "+4°C"], ["plus45", "45°C"]];
  const FIELDS = [["color", "Renk"], ["appearance", "Fiziksel Görünüş"], ["ph", "pH"], ["density", "Yoğunluk"], ["packaging", "Ambalaj/Sızdırmazlık"]];

  const start = async () => {
    try { await api.post(`/qc-tests/${testId}/start`, {}); await load(); onChanged?.(); }
    catch (e) { alert(e?.response?.data?.detail || "Başlatılamadı"); }
  };
  const doComplete = async () => {
    try {
      await api.post(`/qc-tests/${testId}/complete`, { result: completeResult, notes: completeNotes || null });
      setConfirmComplete(false); await load(); onChanged?.();
    } catch (e) { alert(e?.response?.data?.detail || "Tamamlanamadı"); }
  };
  const saveMeas = async () => {
    const rows = [];
    for (const [k, v] of Object.entries(meas)) {
      const [day, condition, field] = k.split("|");
      // Send evaluation=null so backend can auto-suggest from stability_spec (iter124).
      rows.push({ day: Number(day), condition, field, value: v.value || null, evaluation: v.evaluation || null });
    }
    try {
      const r = await api.put(`/qc-tests/${testId}/measurements`, rows);
      const auto = r?.auto_evaluated_count || 0;
      setAutoInfo(auto > 0 ? `${auto} değerlendirme spec'ten otomatik atandı.` : "");
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Kaydedilemedi"); }
  };
  const genPdf = async () => {
    try { await api.post(`/qc-tests/${testId}/pdf`, {}); await load(); alert("PDF oluşturuldu ve Belgeler'e eklendi"); }
    catch (e) { alert(e?.response?.data?.detail || "PDF oluşturulamadı"); }
  };
  const uploadFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("kind", "external_report");
      await api.post(`/qc-tests/${testId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (er) { alert(er?.response?.data?.detail || "Yüklenemedi"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const downloadFile = async (fid, fname) => {
    try {
      const blob = await api.getBlob(`/qc-tests/${testId}/files/${fid}/download`);
      const url = URL.createObjectURL(blob); const a = document.createElement("a");
      a.href = url; a.download = fname; a.click(); URL.revokeObjectURL(url);
    } catch (e) { alert("İndirilemedi"); }
  };
  const deleteFile = async (fid) => {
    if (!window.confirm("Dosya silinsin mi?")) return;
    try { await api.del(`/qc-tests/${testId}/files/${fid}`); await load(); }
    catch { alert("Silinemedi"); }
  };

  return (
    <Modal open={true} onClose={onClose} title={`Test Detayı — ${test.project_no_snapshot}`} size="full">
      <div className="space-y-4" data-testid="qc-detail-modal">
        <div className="bg-slate-50 border border-slate-200 rounded p-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <div><span className="text-slate-500 uppercase text-[10px] block">Proje No</span><b className="font-mono">{test.project_no_snapshot} · {test.revision_snapshot}</b></div>
          <div><span className="text-slate-500 uppercase text-[10px] block">Ürün</span>{test.product_name_snapshot}</div>
          <div><span className="text-slate-500 uppercase text-[10px] block">Müşteri</span>{test.customer_name_snapshot}</div>
          <div><span className="text-slate-500 uppercase text-[10px] block">Test</span>{test.test_type_label} ({test.is_internal ? "Firma İçi" : "Firma Dışı"})</div>
          <div><span className="text-slate-500 uppercase text-[10px] block">Laboratuvar</span>{test.lab_name || "—"}</div>
          <div><span className="text-slate-500 uppercase text-[10px] block">Başlangıç</span>{test.start_date || test.sent_date || "—"}</div>
          <div><span className="text-slate-500 uppercase text-[10px] block">Bitiş</span>{test.actual_end_date || test.expected_end_date || "—"}</div>
          <div><span className="text-slate-500 uppercase text-[10px] block">Durum</span><span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-bold ${STATUS_BADGE[test.status]}`}>{test.status}</span></div>
        </div>

        <div className="flex flex-wrap gap-2">
          {test.status === "BEKLİYOR" && <Btn onClick={start} className="bg-indigo-600 text-white" data-testid="qc-start"><Play size={12}/> Testi Başlat</Btn>}
          {test.status === "TEST YAPILIYOR" && <Btn onClick={() => setConfirmComplete(true)} className="bg-emerald-600 text-white" data-testid="qc-mark-complete"><CheckCircle2 size={12}/> Tamamlandı</Btn>}
          <label className="cursor-pointer">
            <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded"><Upload size={12}/> {uploading ? "Yükleniyor..." : "Belge Yükle"}</span>
            <input type="file" accept="application/pdf,image/jpeg,image/png" onChange={uploadFile} className="hidden" data-testid="qc-upload"/>
          </label>
          {isStability && <Btn onClick={genPdf} variant="secondary" data-testid="qc-gen-pdf" title="Stabilite PDF Oluştur"><FileText size={12}/></Btn>}
        </div>

        {isStability && (
          <div className="border border-slate-200 rounded p-3">
            <div className="flex justify-between mb-2 flex-wrap gap-2 items-center">
              <div className="font-semibold text-sm"><Beaker size={14} className="inline"/> Stabilite Ölçümleri</div>
              <div className="flex gap-2 items-center">
                {autoInfo && <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">{autoInfo}</span>}
                <Btn size="sm" variant="secondary" onClick={() => setSpecOpen(true)} data-testid="qc-spec-edit"><ClipboardList size={12}/> Stabilite Şablonu</Btn>
                <Btn size="sm" onClick={saveMeas} className="bg-emerald-600 text-white" data-testid="qc-save-meas"><CheckCircle2 size={12}/> Ölçümleri Kaydet</Btn>
              </div>
            </div>
            {test.stability_spec && (
              <div className="mb-2 text-[10px] bg-indigo-50 border border-indigo-200 rounded p-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2" data-testid="qc-spec-summary">
                <div><b>pH:</b> {test.stability_spec.ph_min ?? "—"}–{test.stability_spec.ph_max ?? "—"}</div>
                <div><b>Yoğunluk:</b> {test.stability_spec.density_min ?? "—"}–{test.stability_spec.density_max ?? "—"}</div>
                <div><b>Renk:</b> {test.stability_spec.color_expected || "—"}</div>
                <div><b>Görünüm:</b> {test.stability_spec.appearance_expected || "—"}</div>
                <div><b>Ambalaj:</b> {test.stability_spec.packaging_expected || "—"}</div>
              </div>
            )}
            {DAYS.map(day => (
              <details key={day} className="mb-2 border border-slate-200 rounded" open={day === 1}>
                <summary className="cursor-pointer bg-slate-100 px-2 py-1.5 text-sm font-semibold" data-testid={`qc-day-${day}`}>{day}. GÜN</summary>
                <div className="p-2 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                      <tr>
                        <th className="text-left p-1">Analiz</th>
                        {CONDS.map(([ck, cl]) => <th key={ck} className="text-left p-1" colSpan={2}>{cl}</th>)}
                      </tr>
                      <tr>
                        <th></th>
                        {CONDS.flatMap(([ck]) => [<th key={`${ck}-v`} className="text-left p-1">Değer</th>, <th key={`${ck}-e`} className="text-left p-1">Değerlendirme</th>])}
                      </tr>
                    </thead>
                    <tbody>
                      {FIELDS.map(([fk, fl]) => (
                        <tr key={fk} className="border-t border-slate-100">
                          <td className="p-1 font-semibold">{fl}</td>
                          {CONDS.map(([ck]) => {
                            const k = `${day}|${ck}|${fk}`;
                            const v = meas[k] || {};
                            const isBad = v.evaluation === "uygun_degil";
                            return (
                              <React.Fragment key={ck}>
                                <td className={`p-1 ${isBad ? "bg-rose-50" : ""}`}>
                                  <input value={v.value || ""} onChange={e => setMeas(prev => ({ ...prev, [k]: { ...prev[k], value: e.target.value } }))}
                                    className="w-full px-1 py-0.5 border border-slate-200 rounded" data-testid={`qc-m-${day}-${ck}-${fk}-val`}/>
                                </td>
                                <td className="p-1">
                                  <select value={v.evaluation || ""} onChange={e => setMeas(prev => ({ ...prev, [k]: { ...prev[k], evaluation: e.target.value || null } }))}
                                    className={`w-full px-1 py-0.5 border rounded ${isBad ? "border-rose-400 bg-rose-50 text-rose-800 font-semibold" : "border-slate-200"}`}
                                    data-testid={`qc-m-${day}-${ck}-${fk}-eval`}>
                                    <option value="">—</option>
                                    <option value="uygun">Uygun</option>
                                    <option value="uygun_degil">Uygun Değil</option>
                                    <option value="degerlendirilemedi">Değerlendirilemedi</option>
                                  </select>
                                </td>
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </div>
        )}

        <div className="border border-slate-200 rounded p-3">
          <div className="font-semibold text-sm mb-2"><FileText size={14} className="inline"/> Belgeler ({(test.files || []).length})</div>
          {(test.files || []).length === 0 && <div className="text-xs text-slate-400 py-2">Henüz belge yok.</div>}
          <table className="w-full text-xs" data-testid="qc-files">
            <tbody>
              {(test.files || []).map(f => (
                <tr key={f.id} className="border-t border-slate-100">
                  <td className="p-1">{f.filename}</td>
                  <td className="p-1 text-[10px] text-slate-500">{Math.round((f.size || 0) / 1024)} KB · {f.kind === "internal_pdf" ? "Otomatik PDF" : f.kind === "external_report" ? "Dış Lab Raporu" : "Diğer"}</td>
                  <td className="p-1 text-[10px] text-slate-500">{(f.uploaded_at || "").slice(0, 10)} · {f.uploaded_by_name}</td>
                  <td className="p-1 text-right">
                    <button onClick={() => downloadFile(f.id, f.filename)} className="text-indigo-700 hover:underline mr-2" data-testid={`qc-file-download-${f.id}`}><Download size={12} className="inline"/> İndir</button>
                    <button onClick={() => deleteFile(f.id)} className="text-rose-600 hover:underline"><Trash2 size={12} className="inline"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end">
          <Btn variant="secondary" onClick={onClose}>Kapat</Btn>
        </div>

        {confirmComplete && (
          <Modal open={true} onClose={() => setConfirmComplete(false)} title="Testi Tamamla" size="md">
            <div className="space-y-3">
              <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2">Testin sonucunu seçin. Tamamlandıktan sonra sonuç değiştirilemez.</div>
              <select value={completeResult} onChange={e => setCompleteResult(e.target.value)}
                className="w-full px-2 py-1.5 border border-slate-300 rounded" data-testid="qc-complete-result">
                <option value="uygun">Uygun</option>
                <option value="uygun_degil">Uygun Değil</option>
                <option value="degerlendirilemedi">Değerlendirilemedi</option>
              </select>
              <Textarea label="Açıklama" value={completeNotes} onChange={e => setCompleteNotes(e.target.value)} rows={2}/>
              <div className="flex justify-end gap-2">
                <Btn variant="secondary" onClick={() => setConfirmComplete(false)}>Vazgeç</Btn>
                <Btn onClick={doComplete} className="bg-emerald-600 text-white" data-testid="qc-complete-confirm">Tamamla</Btn>
              </div>
            </div>
          </Modal>
        )}
        {specOpen && (
          <StabilitySpecModal rdSampleId={test.rd_sample_id} onClose={() => setSpecOpen(false)} onSaved={() => { setSpecOpen(false); load(); }}/>
        )}
      </div>
    </Modal>
  );
}

function StabilitySpecModal({ rdSampleId, onClose, onSaved }) {
  const [f, setF] = useState({
    ph_min: "", ph_max: "", density_min: "", density_max: "",
    color_expected: "", appearance_expected: "", packaging_expected: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const s = await api.get(`/rd-samples/${rdSampleId}/stability-spec`);
        setF({
          ph_min: s.ph_min ?? "", ph_max: s.ph_max ?? "",
          density_min: s.density_min ?? "", density_max: s.density_max ?? "",
          color_expected: s.color_expected || "",
          appearance_expected: s.appearance_expected || "",
          packaging_expected: s.packaging_expected || "",
          notes: s.notes || "",
        });
      } catch {}
    })();
  }, [rdSampleId]);
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {};
      for (const k of ["ph_min", "ph_max", "density_min", "density_max"]) {
        const v = f[k]; payload[k] = (v === "" || v === null) ? null : parseFloat(String(v).replace(",", "."));
      }
      for (const k of ["color_expected", "appearance_expected", "packaging_expected", "notes"]) {
        payload[k] = f[k] || null;
      }
      await api.put(`/rd-samples/${rdSampleId}/stability-spec`, payload);
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Kaydedilemedi"); setBusy(false); }
  };
  return (
    <Modal open={true} onClose={onClose} title="Stabilite Şablonu (Ürün Hedef Değerleri)" size="lg">
      <div className="space-y-3" data-testid="qc-spec-modal">
        <div className="text-xs bg-indigo-50 border border-indigo-200 rounded p-2">
          Bu ürün için hedef aralıkları tanımlayın. Yeni ölçümde <b>Değerlendirme</b> boş bırakılırsa,
          backend otomatik olarak <b>Uygun / Uygun Değil</b> önerir. Kullanıcı isterse override edebilir.
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold block mb-1">pH Aralığı</label>
            <div className="flex gap-1">
              <input type="number" step="0.01" placeholder="Min" value={f.ph_min} onChange={e => setF({ ...f, ph_min: e.target.value })}
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" data-testid="qc-spec-ph-min"/>
              <span className="self-center text-slate-400">—</span>
              <input type="number" step="0.01" placeholder="Max" value={f.ph_max} onChange={e => setF({ ...f, ph_max: e.target.value })}
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" data-testid="qc-spec-ph-max"/>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold block mb-1">Yoğunluk Aralığı (g/mL)</label>
            <div className="flex gap-1">
              <input type="number" step="0.001" placeholder="Min" value={f.density_min} onChange={e => setF({ ...f, density_min: e.target.value })}
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" data-testid="qc-spec-den-min"/>
              <span className="self-center text-slate-400">—</span>
              <input type="number" step="0.001" placeholder="Max" value={f.density_max} onChange={e => setF({ ...f, density_max: e.target.value })}
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" data-testid="qc-spec-den-max"/>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input label="Beklenen Renk" value={f.color_expected} onChange={e => setF({ ...f, color_expected: e.target.value })} data-testid="qc-spec-color"/>
          <Input label="Beklenen Görünüm" value={f.appearance_expected} onChange={e => setF({ ...f, appearance_expected: e.target.value })} data-testid="qc-spec-appear"/>
          <Input label="Ambalaj" value={f.packaging_expected} onChange={e => setF({ ...f, packaging_expected: e.target.value })}/>
        </div>
        <Textarea label="Notlar" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} rows={2}/>
        {err && <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}
        <div className="text-[10px] text-slate-500 italic">
          NOT: Şablon <b>Ar-Ge projesi</b> seviyesinde saklanır. Bu şablon oluşturulduktan sonra yeni testler otomatik olarak snapshot alır — mevcut testler geriye dönük değişmez.
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
          <Btn onClick={save} disabled={busy} className="bg-indigo-600 text-white" data-testid="qc-spec-save">
            {busy ? "Kaydediliyor..." : "Şablonu Kaydet"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
